import MuiAlert from "@mui/lab/Alert";
import {
	Autocomplete,
	Button,
	Checkbox,
	CircularProgress,
	Grid2,
	InputAdornment,
	Snackbar,
	TextField,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isNil } from "lodash-es";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../../components/modals/error-modal";
import MesfModal from "../../../../../../components/modals/modal.mesf";
import { useTrendingContextV2 } from "../../context/TrendingContextV2";
import type { View } from "../../models/TrendingModelsV2";
import {
	deleteView,
	upsertView,
	upsertViewTag,
} from "../../repository/TrendingRepositoryV2";
import {
	removeViewFromLocal,
	saveViewToLocal,
} from "../../utils/LocalStorageUtils";
import { isDuplicateViewName } from "../../utils/viewNameValidation";

interface Props {
	open: boolean;
	mode: "create" | "update" | "delete";
	handleClose: (shouldUpdate: boolean) => void;
	view?: View | null;
}

interface FormData {
	ViewId: number | null;
	ViewName: string;
	IsPublic: boolean;
}

export const SaveUpdateDeleteViewModalV2 = ({
	open,
	mode,
	handleClose,
	view,
}: Props) => {
	const queryClient = useQueryClient();

	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [error, setError] = useState("");
	const [duplicateViewName, setDuplicateViewName] = useState(false);

	const {
		state: { viewTags, views, viewSelected },
		actions: { setViewSelected },
	} = useTrendingContextV2();

	const {
		register,
		control,
		handleSubmit,
		reset,
		setValue,
		watch,
		formState: { errors },
	} = useForm<FormData>({
		defaultValues: {
			ViewId: null,
			ViewName: "",
			IsPublic: true,
		},
	});

	const watchViewName = watch("ViewName");

	useEffect(() => {
		setDuplicateViewName(false);
	}, [watchViewName]);

	const title = useMemo(() => {
		switch (mode) {
			case "update":
				return "RENAME AND UPDATE";
			case "delete":
				return "DELETE VIEW";
			case "create":
			default:
				return "SAVE VIEW AS";
		}
	}, [mode]);

	const handleSnackbarClose = (
		event?: React.SyntheticEvent | Event,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarOpen(false);
	};

	const updateViewTag = useMutation({
		mutationFn: ({
			viewId,
			tagId,
			color,
			minScale,
			maxScale,
			isVisible,
			isAutoScale,
			unit,
			order,
			alias,
			tagType,
			sourceTagId,
			bitIndex,
		}: {
			viewId: number;
			tagId: number;
			color: string;
			minScale: number;
			maxScale: number;
			isVisible: boolean;
			isAutoScale: boolean;
			unit: string;
			order?: number;
			alias?: string;
			tagType?: string;
			sourceTagId?: number;
			bitIndex?: number;
		}) =>
			upsertViewTag({
				viewId,
				tagId,
				color,
				minScale,
				maxScale,
				isVisible,
				isAutoScale,
				unit,
				order,
				alias,
				tagType,
				sourceTagId,
				bitIndex,
			}),
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const removeView = useMutation({
		mutationFn: (viewId: number) => deleteView(viewId),
		onSuccess: async () => {
			if (view?.ViewId) {
				removeViewFromLocal(view.ViewId);
			} else if (viewSelected?.ViewId) {
				removeViewFromLocal(viewSelected.ViewId);
			}
			setSnackbarMessage("The view was deleted successfully");
			setSnackbarOpen(true);
			await queryClient.invalidateQueries({ queryKey: ["views-v2"] });
			handleClose(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const saveOrUpdateView = useMutation({
		mutationFn: async ({
			viewId,
			viewName,
			isPublic,
		}: {
			viewId: number | null;
			viewName: string;
			isPublic: boolean;
		}): Promise<number> => upsertView({ viewId, viewName, isPublic }),
		onSuccess: async (createdViewId: number, { viewId, viewName, isPublic }) => {
			// UpsertView only returns an id when it inserts, so on update keep the known one
			const savedViewId = viewId ?? createdViewId;

			if (!savedViewId) {
				setError("The view was not saved");
				return;
			}

			// An update only renames the view, its tags are saved from the tags manager
			if (mode !== "update") {
				// Save complete state to local storage (metadata + virtual tags)
				saveViewToLocal(savedViewId, viewTags);

				// Filter out bit-extracted tags for backend persistence
				const backendTags = Object.values(viewTags).filter(
					({ viewTag }) => !viewTag.IsBitExtracted,
				);

				const results = await Promise.allSettled(
					backendTags
						.sort((a, b) => a.order - b.order)
						.map(async ({ viewTag, order }) => {
							await updateViewTag.mutateAsync({
								viewId: savedViewId,
								tagId: viewTag.TagId,
								color: viewTag.Color,
								minScale: viewTag.MinScale,
								maxScale: viewTag.MaxScale,
								isVisible: viewTag.IsVisible,
								isAutoScale: viewTag.IsAutoScale,
								unit: viewTag.Unit,
								order: order,
								alias: viewTag.Alias,
								tagType: viewTag.TagType,
								sourceTagId: viewTag.SourceTagId,
								bitIndex: viewTag.BitIndex,
							});
						}),
				);

				const failed = results.filter((r) => r.status === "rejected");
				if (failed.length > 0) {
					setError("Some tags failed to save");
					return;
				}
			}

			setSnackbarMessage(
				mode === "update"
					? "The view was updated successfully"
					: "The view was created successfully",
			);
			setSnackbarOpen(true);
			await queryClient.invalidateQueries({ queryKey: ["views-v2"] });

			if (
				mode === "create" ||
				(mode === "update" && viewSelected?.ViewId === savedViewId)
			) {
				setViewSelected({
					ViewId: savedViewId,
					ViewName: viewName,
					IsPublic: isPublic,
				});
			}

			handleClose(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const onSubmit: SubmitHandler<FormData> = async (data) => {
		setIsSubmitLoading(true);

		if (mode === "delete") {
			const viewToDelete = view || viewSelected;
			if (!isNil(viewToDelete)) {
				removeView.mutate(viewToDelete.ViewId);
			}
		} else {
			const viewToUpdate = mode === "update" ? view || viewSelected : null;

			if (
				isDuplicateViewName(data.ViewName, views, viewToUpdate?.ViewId ?? null)
			) {
				setDuplicateViewName(true);
				setIsSubmitLoading(false);
				return;
			}

			await saveOrUpdateView.mutate({
				viewId: viewToUpdate?.ViewId ?? null,
				viewName: data.ViewName,
				isPublic: data.IsPublic,
			});
		}

		setIsSubmitLoading(false);
	};

	useEffect(() => {
		if (open) {
			if (mode === "update" || mode === "delete") {
				const currentView = view || viewSelected;
				if (currentView) {
					reset({
						ViewId: currentView.ViewId,
						ViewName: currentView.ViewName,
						IsPublic: currentView.IsPublic,
					});
				}
			} else if (mode === "create") {
				reset({
					ViewId: null,
					ViewName: "",
					IsPublic: true,
				});
			}
		}
	}, [mode, view, viewSelected, reset, open]);

	return (
		<>
			<MesfModal
				open={open}
				handleClose={() => handleClose(false)}
				maxWidth="md"
				aria-labelledby="contained-modal-title-vcenter"
				title={title}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
						<Grid2 container spacing={1}>
							<Grid2 size={12}>
								{mode === "create" && views.length > 0 && viewSelected ? (
									<Autocomplete
										size="small"
										id="list-of-views"
										options={views}
										defaultValue={viewSelected}
										getOptionKey={(option) =>
											typeof option === "string" ? option : option.ViewId
										}
										getOptionLabel={(option) => {
											if (typeof option === "string") {
												return option;
											} else {
												return option.ViewName;
											}
										}}
										freeSolo
										onChange={(ev, val) => {
											if (val && typeof val !== "string") {
												setViewSelected(val);
												setValue("ViewName", "");
											}
										}}
										onInputChange={(event, newInputValue) => {
											const isValueInViews = views.some(
												(view) => view.ViewName === newInputValue,
											);
											if (!isValueInViews) {
												setValue("ViewName", newInputValue);
											}
										}}
										style={{ width: "100%" }}
										renderInput={(params) => (
											<TextField
												{...params}
												{...register("ViewName", {
													required: true,
													maxLength: 100,
												})}
												label="Views"
												variant="outlined"
												error={!!errors.ViewName || duplicateViewName}
												helperText={
													errors.ViewName?.type === "maxLength"
														? "Maximum length is 100 characters"
														: errors.ViewName?.type === "required"
															? "View name is required"
															: duplicateViewName
																? "A view with this name already exists"
																: ""
												}
												margin="dense"
												autoComplete="off"
											/>
										)}
									/>
								) : (
									<TextField
										{...register("ViewName", {
											required: true,
											maxLength: 100,
										})}
										label="View Name"
										variant="outlined"
										error={!!errors.ViewName || duplicateViewName}
										helperText={
											errors.ViewName?.type === "maxLength"
												? "Maximum length is 100 characters"
												: errors.ViewName?.type === "required"
													? "View name is required"
													: duplicateViewName
														? "A view with this name already exists"
														: ""
										}
										fullWidth
										margin="dense"
										autoComplete="off"
										disabled={mode === "delete"}
									/>
								)}
							</Grid2>

							<Grid2 size={{ xs: 12, md: 6 }}>
								<Controller
									name="IsPublic"
									control={control}
									render={({ field }) => (
										<div
											onClick={() =>
												mode !== "delete" && field.onChange(!field.value)
											}
											style={{
												cursor: mode !== "delete" ? "pointer" : "default",
											}}
										>
											<TextField
												fullWidth
												variant="outlined"
												label="Public"
												value=""
												disabled
												sx={{
													userSelect: "none",
													"& .MuiOutlinedInput-root": {
														height: "51px",
													},
													margin: "8px 0px",
												}}
												InputLabelProps={{
													style: {
														color: mode === "delete" ? "#a4a4a4" : "black",
														fontWeight: mode === "delete" ? 500 : 400,
													},
												}}
												InputProps={{
													endAdornment: (
														<InputAdornment position="end">
															<Checkbox
																checked={!!field.value}
																onChange={(e) =>
																	field.onChange(e.target.checked)
																}
																color="primary"
																disabled={mode === "delete"}
																size="small"
																style={{
																	cursor:
																		mode !== "delete" ? "pointer" : "default",
																}}
															/>
														</InputAdornment>
													),
												}}
											/>
										</div>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => handleClose(false)}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={
										isSubmitLoading ||
										duplicateViewName ||
										(mode !== "delete" && !watchViewName?.trim())
									}
									variant="contained"
									color={mode === "delete" ? "secondary" : "primary"}
									type="submit"
								>
									{mode === "delete"
										? "Delete"
										: mode === "update"
											? "Save"
											: "Save"}
								</Button>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>

			<Snackbar
				open={snackbarOpen}
				autoHideDuration={2500}
				onClose={handleSnackbarClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<MuiAlert
					elevation={6}
					variant="filled"
					severity="success"
					onClose={handleSnackbarClose}
				>
					{snackbarMessage}
				</MuiAlert>
			</Snackbar>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
